import { FeaturedIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  PRIORITY_TASK_STATUS_CODES,
  PRIORITY_TASK_TYPE_CODES,
  PriorityTask,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { TaskCard, TaskCardProps } from './TaskCard';
import { TASK_CARD_MIN_WIDTH } from '../constants/priorities.constants';
import { useGetTaskActionsAndProperties } from '../hooks/tasks.hooks';

const PRIORITY_TASK_EXAMPLE_TASKS_STRINGS =
  appStrings.modules.priorities.unassignedTasksSection.exampleTasks;

const EditIcon = () => (
  <FeaturedIcon
    iconName="Edit05"
    size="md"
    borderColor="Colors/Base/Black"
    color="Colors/Base/Black"
  />
);
const TaskInstructionalCard = ({ onClick }: { onClick: () => void }) => {
  return (
    <Box sx={taskContainerSx} onClick={onClick}>
      <Box sx={taskHeaderContainerSx}>
        <Box sx={taskHeaderContentSx}>
          <Typography variant="text-lg-semibold" sx={taskTitleSx}>
            {PRIORITY_TASK_EXAMPLE_TASKS_STRINGS.title}
          </Typography>

          <Typography variant="text-sm-regular">
            {PRIORITY_TASK_EXAMPLE_TASKS_STRINGS.description}
          </Typography>
        </Box>

        <EditIcon />
      </Box>
    </Box>
  );
};

export const UnassignedTasksEmptyState = () => {
  const { taskTypes, taskStatuses, handleOpenNewTaskDialog } = useGetTaskActionsAndProperties();

  const sharedParams = {
    taskTypes,
    taskStatuses,
    openEditTaskDialog: () => null,
    setCurrentDialogNugget: () => null,
    updateTask: () => Promise.resolve(),
    deleteTask: () => null,
    handleDuplicateTask: () => null,
    task: {
      status_code: PRIORITY_TASK_STATUS_CODES.NOT_STARTED,
      type_code: PRIORITY_TASK_TYPE_CODES.SMART_GOAL,
      content_url: null,
      content_id: null,
      user_id: null,
      assigned_by_id: null,
      playlist_id: null,
      created_at: '',
      updated_at: '',
    },
  };

  const exampleTasks: TaskCardProps[] = [
    {
      ...sharedParams,
      task: {
        ...sharedParams.task,
        id: 1,
        title: 'Study for SAT',
        description: '15 minutes per day',
        priority: 3,
        order: 1,
        due_date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
      },
    },
    {
      ...sharedParams,
      task: {
        ...sharedParams.task,
        id: 2,
        title: 'Create a Linkedin Profile',
        description: 'Make a profile and upload one experience',
        status_code: PRIORITY_TASK_STATUS_CODES.NOT_STARTED,
        type_code: PRIORITY_TASK_TYPE_CODES.PERSONAL,
        priority: 1,
        order: 2,
        due_date: new Date(Date.now() + 40 * 24 * 60 * 60 * 1000).toISOString(),
      },
    },
    {
      ...sharedParams,
      task: {
        ...sharedParams.task,
        id: 3,
        title: 'Try the Pomodoro',
        description: '3 x 25 minutes focused study, followed by 5 minutes of rest',
        status_code: PRIORITY_TASK_STATUS_CODES.NOT_STARTED,
        type_code: PRIORITY_TASK_TYPE_CODES.SMART_GOAL,
        priority: 2,
        order: 3,
        due_date: new Date(Date.now() + 29 * 24 * 60 * 60 * 1000).toISOString(),
      },
    },
  ];

  const handleOpeningNewTaskDialog = (preFilledTaskInfo?: Partial<PriorityTask>) => {
    handleOpenNewTaskDialog(null, true, preFilledTaskInfo);
  };

  return (
    <>
      <TaskInstructionalCard onClick={() => handleOpeningNewTaskDialog()} />

      {exampleTasks.map((task) => (
        <TaskCard
          key={task.task.id}
          {...task}
          opacity={0.5}
          onCardClick={() => handleOpeningNewTaskDialog(task.task)}
          disabled={{ disableNuggetClick: false, disableStatus: true }}
          topRightElement={{
            type: 'ELEMENT',
            element: <EditIcon />,
          }}
        />
      ))}
    </>
  );
};

const taskHeaderContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
  gap: backrs.spacing['spacing-2xl'],
});

const taskContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  paddingY: backrs.spacing['spacing-2xl'],
  borderRadius: backrs.radius['radius-lg'],
  maxWidth: {
    xs: '100%',
    sm: `${TASK_CARD_MIN_WIDTH}px`,
  },
  width: '100%',

  transition: 'all 0.3s ease-in-out',

  cursor: 'pointer',
  '&:hover': {
    borderColor: backrs.colors['Colors/Foreground/fg-primary'],
    transform: 'translate(-2px, -2px)',
    boxShadow: backrs.shadows['shadow-lg'],
  },

  '&:active': {
    transform: 'none !important',
    boxShadow: 'none !important',
    transition: 'all 0s !important',
  },
});

const taskHeaderContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  overflow: 'hidden',
  flex: 1,
  paddingX: backrs.spacing['spacing-3xl'],
});

const taskTitleSx: SxProps<Theme> = ({ backrs }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: `calc(${TASK_CARD_MIN_WIDTH}px - (${backrs.spacing['spacing-7xl']}))`,
});
